import { useCallback, useEffect, useState } from 'react';
import { Upload, Wand2, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { AccountSelect, DealSelect, Money, Empty, fmtDate, usd, inputCls, btnCls, btn2Cls, cardCls, thCls, tdCls } from './shared';

// Relay CSV import (never a Relay login) and the categorize queue: bank lines that no rule
// matched sit in "Uncategorized" until Chad picks an account (and a deal, for deal money).

export default function QueueTab({ me, accounts, deals, reloadShared }) {
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setQueue((await api.get('/books/entries?queue=1&limit=1000')).entries); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyRules = async () => {
    try { const r = await api.post('/books/rules/apply', {}); setMsg(`Rules categorized ${r.applied} line(s); ${r.remaining} left.`); load(); } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-4">
      {me.can_edit && <RelayImport accounts={accounts} onDone={(m) => { setMsg(m); load(); reloadShared(); }} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-[var(--text-primary)]">To categorize {queue ? `(${queue.length})` : ''}</h2>
        {me.can_edit && queue?.length > 0 && <button className={btn2Cls} onClick={applyRules}><Wand2 size={14} /> Run rules again</button>}
      </div>
      {msg && <p className="text-sm text-emerald-700 dark:text-emerald-400">{msg}</p>}
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!queue ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p>
        : queue.length === 0 ? <Empty>Nothing to categorize. Import a Relay CSV to bring in new bank lines.</Empty>
          : (
            <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                  <th className={thCls}>Date</th><th className={thCls}>Bank line</th><th className={`${thCls} text-right`}>Amount</th>
                  <th className={thCls}>{me.can_edit ? 'Category and deal' : ''}</th>
                </tr></thead>
                <tbody>
                  {queue.map((e) => <QueueRow key={e.id} e={e} me={me} accounts={accounts} deals={deals} onSaved={(m) => { setMsg(m); load(); }} onError={setError} />)}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );
}

function bankAmount(e, accounts) {
  const cash = new Set(accounts.filter((a) => a.is_cash).map((a) => a.id));
  const l = e.lines.find((x) => cash.has(x.account_id));
  return l ? l.debit_cents - l.credit_cents : 0;
}

function QueueRow({ e, me, accounts, deals, onSaved, onError }) {
  const [accountId, setAccountId] = useState(null);
  const [dealId, setDealId] = useState(null);
  const [rule, setRule] = useState(false);
  const [ruleText, setRuleText] = useState(e.bank_payee || e.counterparty || '');
  const [busy, setBusy] = useState(false);
  const amt = bankAmount(e, accounts);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/books/entries/${e.id}/categorize`, { account_id: accountId, deal_id: dealId, revision: e.updated_at, ...(rule ? { rule: { match_text: ruleText } } : {}) });
      onSaved(`Categorized ${e.bank_payee || 'line'} (${usd(amt)}).`);
    } catch (err) { onError(err.message); } finally { setBusy(false); }
  };
  return (
    <tr className="border-t border-[var(--border)]">
      <td className={`${tdCls} whitespace-nowrap`}>{fmtDate(e.entry_date)}</td>
      <td className={tdCls}>
        <div className="font-medium text-[var(--text-primary)]">{e.bank_payee || e.counterparty || 'No payee'}</div>
        <div className="text-xs text-[var(--text-secondary)]">{[e.bank_description, e.bank_reference].filter(Boolean).join(' / ')}</div>
      </td>
      <td className={`${tdCls} text-right`}><Money cents={amt} /></td>
      <td className={tdCls}>
        {me.can_edit && (
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap gap-1.5">
              <AccountSelect accounts={accounts} value={accountId} onChange={setAccountId} exclude={['9999']} className="min-w-[200px] flex-1" />
              <DealSelect deals={deals} value={dealId} onChange={setDealId} className="min-w-[140px]" />
              <button className={btnCls} disabled={!accountId || busy} onClick={save}><Check size={14} /> Save</button>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
              <input type="checkbox" checked={rule} onChange={(ev) => setRule(ev.target.checked)} />
              Remember: bank lines containing
              <input className={`${inputCls} py-0.5 text-xs`} value={ruleText} onChange={(ev) => setRuleText(ev.target.value)} disabled={!rule} />
              go to this account
            </label>
          </div>
        )}
      </td>
    </tr>
  );
}

function RelayImport({ accounts, onDone }) {
  const cashAccounts = accounts.filter((a) => a.is_cash && a.active);
  const [code, setCode] = useState(cashAccounts[0]?.code || '1000');
  const [filter, setFilter] = useState('');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onFile = async (f) => {
    setPreview(null); setError('');
    if (!f) return;
    if (f.size > 4_000_000) { setError('That file is too large for one import. Export a shorter date range.'); return; }
    setFileName(f.name);
    setCsv(await f.text());
  };
  const run = async (dry) => {
    setBusy(true); setError('');
    try {
      const r = await api.post('/books/import/relay', { csv, account_code: code, account_filter: filter || undefined, dry_run: dry });
      if (dry) setPreview(r);
      else { setPreview(null); setCsv(''); setFileName(''); onDone(`Imported ${r.created} new line(s): ${r.categorized_by_rule} categorized by rules, ${r.to_queue} to categorize.`); }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const acctName = (id) => accounts.find((a) => a.id === id)?.name || '';

  return (
    <div className={cardCls}>
      <h2 className="mb-1 font-semibold text-[var(--text-primary)]">Import a Relay CSV</h2>
      <p className="mb-3 text-xs text-[var(--text-secondary)]">In Relay, export the account's transactions as CSV and upload the file here. Lines already imported, pending lines, and lines on or before the cutover date are skipped.</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className={`${btn2Cls} cursor-pointer`}><Upload size={14} /> {fileName || 'Choose CSV'}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
        </label>
        <select className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} aria-label="Import into">
          {cashAccounts.map((a) => <option key={a.id} value={a.code}>Into {a.code} {a.name}</option>)}
        </select>
        <input className={`${inputCls} w-44`} placeholder="Relay account # (optional)" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <button className={btn2Cls} disabled={!csv || busy} onClick={() => run(true)}>Preview</button>
        <button className={btnCls} disabled={!csv || busy || !preview} onClick={() => run(false)}>Import</button>
      </div>
      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
      {preview && (
        <div className="mt-3 text-sm">
          <p className="text-[var(--text-primary)]">{preview.to_import} new line(s): {preview.categorized_by_rule} matched a rule, {preview.to_queue} will wait in the queue. {preview.skipped.length} skipped.</p>
          {Object.keys(preview.balance_hints || {}).length > 0 && (
            <p className="text-xs text-[var(--text-secondary)]">Relay running balance at month end: {Object.entries(preview.balance_hints).sort().map(([p, c]) => `${p} ${usd(c)}`).join(', ')}. Use the statement balance for the tie-out.</p>
          )}
          {preview.preview.length > 0 && (
            <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-[var(--border)] text-xs">
              {preview.preview.map((p, i) => (
                <li key={i} className="flex justify-between gap-2 border-b border-[var(--border)] px-2 py-1 last:border-0">
                  <span>{p.date} {p.payee || p.description}</span>
                  <span className="text-[var(--text-secondary)]">{p.by_rule ? acctName(p.account_id) : 'to categorize'}</span>
                  <Money cents={p.amount_cents} />
                </li>
              ))}
            </ul>
          )}
          {preview.skipped.length > 0 && (
            <details className="mt-2 text-xs text-[var(--text-secondary)]"><summary>Skipped lines</summary>
              <ul>{preview.skipped.slice(0, 100).map((s, i) => <li key={i}>Row {s.line}: {s.reason}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
